import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CalendarClock, Trash2, Upload } from "lucide-react";
import { useRef, useState } from "react";

import {
  borrarProyeccion,
  cargarProyecciones,
  getProyecciones,
  vaciarProyecciones,
  type Proyeccion,
} from "@/lib/api";

const money = (n: number | null | undefined, mon: string) =>
  n == null
    ? "—"
    : new Intl.NumberFormat("es-MX", {
        style: "currency", currency: mon, maximumFractionDigits: 2,
      }).format(n);

/*
 * Proyecciones de ingresos/egresos B2B: el calendario de facturación esperado
 * (Excel "Proyección - Transacciones B2B"). Cada carga REEMPLAZA el juego
 * completo; /b2b/utilidad muestra estas proyecciones en los meses donde el
 * cálculo real aún no existe (fila "(proyección cargada)").
 */
export function ProyeccionesPage() {
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [resumenCarga, setResumenCarga] = useState<string | null>(null);

  const q = useQuery({ queryKey: ["proyecciones"], queryFn: getProyecciones });
  const items = q.data?.items ?? [];

  const invalidar = () => {
    qc.invalidateQueries({ queryKey: ["proyecciones"] });
    qc.invalidateQueries({ queryKey: ["b2b-utilidad"] });
  };

  const subir = useMutation({
    mutationFn: (fd: FormData) => cargarProyecciones(fd),
    onSuccess: (r) => {
      setError(null);
      setResumenCarga(
        `${r.cargadas} proyecciones cargadas (${r.mapeadas} mapeadas a contrato` +
          (r.sin_mapear.length ? `; sin mapear: ${r.sin_mapear.join(", ")}` : "") + ").",
      );
      invalidar();
    },
    onError: (e) => setError(e instanceof Error ? e.message : "No se pudo cargar"),
  });

  const borrar = useMutation({
    mutationFn: (id: number) => borrarProyeccion(id),
    onSuccess: invalidar,
    onError: (e) => setError(e instanceof Error ? e.message : "No se pudo borrar"),
  });

  const vaciar = useMutation({
    mutationFn: vaciarProyecciones,
    onSuccess: () => {
      setResumenCarga(null);
      invalidar();
    },
    onError: (e) => setError(e instanceof Error ? e.message : "No se pudo vaciar"),
  });

  const onArchivo = (f: File | null) => {
    if (!f) return;
    if (items.length > 0 &&
        !window.confirm(`Cargar "${f.name}" REEMPLAZA las ${items.length} proyecciones actuales. ¿Continuar?`)) {
      if (fileRef.current) fileRef.current.value = "";
      return;
    }
    const fd = new FormData();
    fd.append("archivo", f);
    subir.mutate(fd);
    if (fileRef.current) fileRef.current.value = "";
  };

  const sinMapear = items.filter((i) => i.id_contrato == null).length;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
            <CalendarClock className="w-5 h-5 text-slate-500" /> Proyecciones B2B
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Calendario de facturación esperado (ingresos y egresos por transacción). Sube el
            Excel con columnas <b>ID | Contraparte | OC | Producto | Tipo | Subtotal | Total |
            Moneda | Fecha de facturación</b> — cada carga <b>reemplaza</b> el juego completo.
            La <b>Utilidad B2B</b> muestra estas proyecciones (subtotal, sin IVA) en los meses
            donde el cálculo real aún no existe; en cuanto hay real, el real manda.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <input
            ref={fileRef}
            type="file"
            accept=".xlsx"
            className="hidden"
            onChange={(e) => onArchivo(e.target.files?.[0] ?? null)}
          />
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={subir.isPending}
            className="inline-flex items-center gap-2 bg-blue-600 text-white rounded px-4 py-2 text-sm hover:bg-blue-700 disabled:opacity-50"
          >
            <Upload className="w-4 h-4" /> {subir.isPending ? "Cargando…" : "Cargar Excel"}
          </button>
          <button
            type="button"
            onClick={() => {
              if (window.confirm(`¿Borrar TODAS las ${items.length} proyecciones?`)) vaciar.mutate();
            }}
            disabled={items.length === 0 || vaciar.isPending}
            className="inline-flex items-center gap-2 border text-slate-600 rounded px-3 py-2 text-sm hover:bg-slate-50 disabled:opacity-50"
          >
            <Trash2 className="w-4 h-4" /> Vaciar
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded p-3 text-sm">{error}</div>
      )}
      {resumenCarga && (
        <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 rounded p-3 text-sm">
          {resumenCarga}
        </div>
      )}
      {sinMapear > 0 && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded p-3 text-sm">
          {sinMapear} proyección(es) <b>sin contrato mapeado</b> (columna Contrato en "—"):
          en Utilidad B2B salen como fila propia sin cruzarse contra el cálculo real. El mapeo
          es automático por (Contraparte, OC) → folio; revisa que la OC del Excel coincida con
          el folio del contrato.
        </div>
      )}

      {q.isLoading && (
        <div className="bg-white border rounded-lg p-8 text-center text-slate-500 text-sm">Cargando…</div>
      )}
      {q.isError && (
        <div className="bg-white border rounded-lg p-8 text-center text-red-600 text-sm">
          {(q.error as Error).message}
        </div>
      )}
      {!q.isLoading && items.length === 0 && (
        <div className="bg-white border rounded-lg p-8 text-center text-slate-500 text-sm">
          Sin proyecciones cargadas — sube el Excel para empezar.
        </div>
      )}

      {items.length > 0 && (
        <div className="bg-white border rounded-lg overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-100 text-slate-600 text-left">
              <tr>
                <th className="px-3 py-2">Fecha fact.</th>
                <th className="px-3 py-2">ID</th>
                <th className="px-3 py-2">Contraparte</th>
                <th className="px-3 py-2">OC</th>
                <th className="px-3 py-2">Contrato</th>
                <th className="px-3 py-2">Producto</th>
                <th className="px-3 py-2">Tipo</th>
                <th className="px-3 py-2 text-right">Subtotal</th>
                <th className="px-3 py-2 text-right">Total (IVA)</th>
                <th className="px-3 py-2 w-8" />
              </tr>
            </thead>
            <tbody>
              {items.map((p: Proyeccion) => (
                <tr key={p.id} className="border-t hover:bg-slate-50">
                  <td className="px-3 py-1.5 tabular-nums">{p.fecha_facturacion}</td>
                  <td className="px-3 py-1.5 font-mono text-xs">{p.clave ?? "—"}</td>
                  <td className="px-3 py-1.5">{p.contraparte}</td>
                  <td className="px-3 py-1.5">{p.oc ?? "—"}</td>
                  <td className="px-3 py-1.5">
                    {p.folio ? (
                      <span className="font-medium text-slate-700">{p.folio}</span>
                    ) : (
                      <span className="text-amber-600" title="Sin contrato mapeado">—</span>
                    )}
                  </td>
                  <td className="px-3 py-1.5">{p.producto}</td>
                  <td className="px-3 py-1.5">
                    <span
                      className={`inline-block text-[11px] font-medium rounded-full px-2 py-0.5 ${
                        p.tipo === "ingreso"
                          ? "bg-emerald-100 text-emerald-700"
                          : "bg-amber-100 text-amber-700"
                      }`}
                    >
                      {p.tipo === "ingreso" ? "Ingreso" : "Egreso"}
                    </span>
                  </td>
                  <td className="px-3 py-1.5 text-right tabular-nums">
                    {money(p.subtotal, p.moneda)}
                  </td>
                  <td className="px-3 py-1.5 text-right tabular-nums text-slate-500">
                    {money(p.total, p.moneda)}
                  </td>
                  <td className="px-2 py-1.5">
                    <button
                      type="button"
                      title="Borrar esta proyección"
                      onClick={() => borrar.mutate(p.id)}
                      className="text-slate-400 hover:text-red-600"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="px-3 py-2 border-t text-xs text-slate-500">
            {items.length} proyecciones · {items.length - sinMapear} mapeadas a contrato
            {items[0] && ` · cargadas por ${items[0].cargado_por ?? "—"} el ${
              new Date(items[0].cargado_en).toLocaleString("es-MX", {
                day: "2-digit", month: "short", year: "numeric",
                hour: "2-digit", minute: "2-digit",
              })}`}
          </div>
        </div>
      )}
    </div>
  );
}
